// Top Qualifier Settings Component
// Shows TQ tenant info, hosted URL, and embed snippet
// Displayed in Profile Settings > Overview tab

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface TQTenant {
  tq_tenant_id: string;
  tq_customer_slug: string;
  embed_key: string;
  hosted_url: string;
  status: string;
  provisioned_at: string;
  stripe_subscription_id?: string;
}

interface TopQualifierSettingsProps {
  userId: string;
}

const TopQualifierSettings: React.FC<TopQualifierSettingsProps> = ({ userId }) => {
  const [tenant, setTenant] = useState<TQTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    fetchTenantInfo();
  }, [userId]);

  const fetchTenantInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's company_id
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('company_id, tq_enabled')
        .eq('id', userId)
        .single();

      if (userError) throw userError;
      if (!user?.tq_enabled) {
        setLoading(false);
        return; // Don't show component if TQ not enabled
      }

      // Fetch tenant info
      const { data: tenantData, error: tenantError } = await supabase
        .from('tq_tenants')
        .select('*')
        .eq('company_id', user.company_id)
        .single();

      if (tenantError) {
        if (tenantError.code === 'PGRST116') {
          // No tenant found
          setError('Top Qualifier tenant not found. Provisioning may still be in progress.');
        } else {
          throw tenantError;
        }
      } else {
        setTenant(tenantData);
      }
    } catch (err) {
      console.error('Error fetching TQ tenant info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tenant information');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="border rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              Top Qualifier Setup In Progress
            </h3>
            <p className="mt-1 text-sm text-yellow-700">
              {error || 'Your TQ tenant is being provisioned. This usually takes a few minutes. Please refresh the page in a moment.'}
            </p>
            <button
              onClick={fetchTenantInfo}
              className="mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-900"
            >
              Refresh Status →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const embedSnippet = `<script
  src="https://top-qualifier-api.onrender.com/embed/tq.js"
  data-tq-key="${tenant.embed_key}"
  data-tq-position="bottom-right"
></script>`;

  return (
    <div className="border rounded-lg p-6 bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2 text-2xl">🤖</span>
            Top Qualifier AI
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            AI-powered lead qualification and engagement
          </p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
          tenant.status === 'active'
            ? 'bg-green-100 text-green-800'
            : tenant.status === 'suspended'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          <span className={`w-2 h-2 rounded-full mr-2 ${
            tenant.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
          }`}></span>
          {tenant.status}
        </span>
      </div>

      {/* Tenant Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tenant ID</label>
          <div className="flex items-center space-x-2">
            <code className="flex-1 px-3 py-2 bg-white border rounded text-sm font-mono text-gray-800">
              {tenant.tq_tenant_id}
            </code>
            <button
              onClick={() => copyToClipboard(tenant.tq_tenant_id, 'tenant_id')}
              className="px-3 py-2 text-sm text-purple-600 hover:bg-purple-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === 'tenant_id' ? '✓' : '📋'}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Slug</label>
          <div className="flex items-center space-x-2">
            <code className="flex-1 px-3 py-2 bg-white border rounded text-sm font-mono text-gray-800">
              {tenant.tq_customer_slug}
            </code>
            <button
              onClick={() => copyToClipboard(tenant.tq_customer_slug, 'slug')}
              className="px-3 py-2 text-sm text-purple-600 hover:bg-purple-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === 'slug' ? '✓' : '📋'}
            </button>
          </div>
        </div>
      </div>

      {/* Hosted URL */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-gray-600 mb-2">Hosted Chat URL</label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={tenant.hosted_url}
            readOnly
            className="flex-1 px-3 py-2 bg-white border rounded text-sm text-gray-800"
          />
          <button
            onClick={() => copyToClipboard(tenant.hosted_url, 'hosted_url')}
            className="px-3 py-2 text-sm bg-purple-600 text-white hover:bg-purple-700 rounded transition-colors"
          >
            {copiedField === 'hosted_url' ? 'Copied!' : 'Copy'}
          </button>
          <a
            href={tenant.hosted_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
          >
            Open ↗
          </a>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          This is your public-facing chat interface. Share this link with your leads.
        </p>
      </div>

      {/* Embed Snippet */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-gray-600 mb-2">Website Embed Code</label>
        <div className="relative">
          <pre className="p-3 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto font-mono">
{embedSnippet}
          </pre>
          <button
            onClick={() => copyToClipboard(embedSnippet, 'embed')}
            className="absolute top-2 right-2 px-3 py-1 text-xs bg-gray-700 text-white hover:bg-gray-600 rounded transition-colors"
          >
            {copiedField === 'embed' ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Paste this code before the closing <code>&lt;/body&gt;</code> tag on your website to add the chat widget.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-purple-100">
        <a
          href="/dashboard"
          className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
        >
          🚀 Open TQ Dashboard
        </a>
        <a
          href="https://docs.standingencore.com/top-qualifier"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 text-sm bg-white text-purple-600 border border-purple-200 rounded hover:bg-purple-50 transition-colors"
        >
          📖 View Documentation
        </a>
        {tenant.stripe_subscription_id && (
          <button
            onClick={() => window.open('/billing', '_blank')}
            className="px-4 py-2 text-sm bg-white text-gray-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
          >
            💳 Manage Subscription
          </button>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
        <p className="text-xs text-blue-800">
          <strong>💡 Pro tip:</strong> Use the Dashboard tile on your main dashboard for quick access to TQ, or embed the chat widget on your website to engage with leads automatically.
        </p>
      </div>
    </div>
  );
};

export default TopQualifierSettings;
